import { GrowthBook } from '@growthbook/growthbook-react'
import * as Sentry from '@sentry/nextjs'
import {
  FEATURE_FLAGS_API_HOST,
  isFeatureFlagsConfigured,
  resolveFeatureFlagsClientKey,
} from './env'

export const featureFlagsClient = new GrowthBook({
  apiHost: FEATURE_FLAGS_API_HOST,
  clientKey: resolveFeatureFlagsClientKey(),
  enabled: isFeatureFlagsConfigured(),
  enableDevMode: process.env.NODE_ENV === 'development',
})

type FeatureFlagsAvailabilityState = 'pending' | 'available' | 'unavailable'

let featureFlagsAvailabilityState: FeatureFlagsAvailabilityState = isFeatureFlagsConfigured()
  ? 'pending'
  : 'unavailable'

const availabilityListeners = new Set<() => void>()

export const subscribeFeatureFlagsAvailability = (listener: () => void): (() => void) => {
  availabilityListeners.add(listener)
  return () => {
    availabilityListeners.delete(listener)
  }
}

export const getFeatureFlagsAvailability = (): boolean =>
  featureFlagsAvailabilityState !== 'unavailable'

const setFeatureFlagsAvailability = (state: FeatureFlagsAvailabilityState): void => {
  featureFlagsAvailabilityState = state
  availabilityListeners.forEach(listener => listener())
}

const runInitializeFeatureFlags = async (): Promise<void> => {
  if (!isFeatureFlagsConfigured()) {
    setFeatureFlagsAvailability('unavailable')

    if (process.env.NODE_ENV === 'development') {
      console.warn('Feature flags are not configured, falling back to defaults')
    }
    return
  }

  try {
    const response = await featureFlagsClient.init({
      skipCache: true,
      streaming: true,
      timeout: 1500,
    })

    setFeatureFlagsAvailability(response.success !== false ? 'available' : 'unavailable')
  } catch (error) {
    console.warn('Feature flags initialization failed, falling back to defaults', error)
    Sentry.captureException(error, {
      level: 'warning',
      extra: { context: 'feature-flags-client-init' },
    })
    setFeatureFlagsAvailability('unavailable')
  }
}

let initializeFeatureFlagsPromise: Promise<void> | undefined

export const initializeFeatureFlags = async (): Promise<void> => {
  initializeFeatureFlagsPromise ??= runInitializeFeatureFlags()
  return initializeFeatureFlagsPromise
}
